import { StandardError } from "@core-ts/StandardError"
import { BaseLazyAsyncIterator } from "@core-ts/iter/async"

type QueueOptions = {
    batchSize?: number | [number] | [number, number]
    maxLength?: number
    maxLengthCallback?: (count: number) => void
}

export class DisposableQueue<T> extends BaseLazyAsyncIterator<T[]> {
    private buf: T[] = []
    private queue: ((value: T[]) => void)[] = []
    private minBatchSize = 1
    private maxBatchSize = 1
    private maxLength?: number
    private maxLengthCallback?: (count: number) => void

    constructor({ batchSize, maxLength, maxLengthCallback }: QueueOptions) {
        super()
        if (maxLengthCallback && !maxLength) {
            throw new StandardError("Cannot create a disposable queue with a maxLengthCallback but without a maxLength")
        }

        this.maxLength = maxLength
        this.maxLengthCallback = maxLengthCallback

        if (Array.isArray(batchSize)) {
            if (batchSize.length === 1) {
                this.minBatchSize = batchSize[0]
                this.maxBatchSize = batchSize[0]
            } else if (batchSize[0] < batchSize[1]) {
                this.minBatchSize = batchSize[0]
                this.maxBatchSize = batchSize[1]
            } else {
                this.minBatchSize = batchSize[1]
                this.maxBatchSize = batchSize[0]
            }
        } else {
            this.minBatchSize = batchSize ?? 1
            this.maxBatchSize = batchSize ?? 1
        }
    }

    push(...data: T[]): void {
        this.buf.push(...data)

        while (this.queue.length > 0 && this.buf.length >= this.minBatchSize) {
            let next: T[]
            if (this.buf.length > this.maxBatchSize) {
                next = this.buf.splice(0, this.maxBatchSize)
            } else {
                next = this.buf.splice(0)
            }

            this.queue.splice(0, 1)[0](next)
        }

        if (this.maxLength && this.buf.length > this.maxLength) {
            if (this.maxLengthCallback) {
                this.maxLengthCallback(this.buf.length)
            }

            this.buf.splice(0)
        }
    }

    async next(): Promise<IteratorResult<T[], never>> {
        if (this.buf.length >= this.minBatchSize) {
            if (this.buf.length > this.maxBatchSize) {
                return { done: false, value: this.buf.splice(0, this.maxBatchSize) }
            }
            return { done: false, value: this.buf.splice(0) }
        }

        return new Promise((resolve) => {
            this.queue.push((value) => {
                resolve({ done: false, value })
            })
        })
    }

    tryNext(): T[] | undefined {
        if (this.buf.length < this.minBatchSize) return undefined
        if (this.buf.length > this.maxBatchSize) return this.buf.splice(0, this.maxBatchSize)
        return this.buf.splice(0)
    }
}
